import * as React from 'react';
import { useContext } from 'react';
import * as Immutable from 'immutable';

import InteractiveContext from 'views/components/contexts/InteractiveContext';
import useMessage from 'views/hooks/useMessage';
import type {
  Evidence,
  LogEvidence,
  EvidenceFilters,
} from 'security-app/components/Investigations/Details/TimelineSegment/types';
import MessagePermalinkButton from 'views/components/common/MessagePermalinkButton';
import FieldTypesContext from 'views/components/contexts/FieldTypesContext';
import TypeSpecificValue from 'views/components/TypeSpecificValue';
import FieldTypeMapping from 'views/logic/fieldtypes/FieldTypeMapping';
import FieldType from 'views/logic/fieldtypes/FieldType';
import SingleMessageFieldTypesProvider from 'views/components/fieldtypes/SingleMessageFieldTypesProvider';
import { Spinner } from 'components/common';
import MessageFieldsFilter from 'logic/message/MessageFieldsFilter';
import type { Message } from 'views/components/messagelist/Types';
import StreamsContext from 'contexts/StreamsContext';
import MessageSummary from 'search/MessageSummary';

import EvidenceFields from './EvidenceFields';
import EvidenceCardFoundation from './EvidenceCardFoundation';

type LogCardFieldsProps = {
  fields: Message['fields'];
  similarFieldsFilter: EvidenceFilters['similar'];
  evidenceId: Evidence['id'];
};

const LogCardFields = ({ fields, similarFieldsFilter, evidenceId }: LogCardFieldsProps) => {
  const { streams: messageStreams, timestamp } = fields;
  const streams = useContext(StreamsContext);
  const streamsMap = Immutable.Map(Object.fromEntries(streams.map((stream) => [stream.id, stream])));
  const fieldTypesStreams = messageStreams?.filter((streamId) => streamsMap.has(streamId));
  const { matching_fields: similarFields } = similarFieldsFilter?.fields.logs.find(
    ({ message: { id } }) => id === evidenceId,
  ) ?? { matching_fields: [] };

  return (
    <SingleMessageFieldTypesProvider streams={fieldTypesStreams} timestamp={timestamp}>
      <FieldTypesContext.Consumer>
        {({ all }) => {
          if (all.size === 0) {
            return <Spinner />;
          }

          return (
            <EvidenceFields
              similarFieldsFilterValue={similarFields}
              renderFieldValue={(fieldName, fieldValue) => (
                <TypeSpecificValue
                  field={fieldName}
                  value={fieldValue}
                  type={
                    all?.find(
                      (t) => t.name === fieldName,
                      undefined,
                      FieldTypeMapping.create(fieldName, FieldType.Unknown),
                    )?.type
                  }
                />
              )}
              fields={fields}
            />
          );
        }}
      </FieldTypesContext.Consumer>
    </SingleMessageFieldTypesProvider>
  );
};

const EvidenceLogCard = ({
  evidence,
  onResetShowSimilar,
  onShowSimilar,
  similarFieldsFilter,
}: {
  evidence: LogEvidence;
  onResetShowSimilar: () => void;
  onShowSimilar: (targetEvidence: Evidence) => Promise<void>;
  similarFieldsFilter: EvidenceFilters['similar'];
}) => {
  const { data: message, isInitialLoading } = useMessage(evidence.entity.index, evidence.id);
  const filteredMessageFields = message?.fields ? MessageFieldsFilter.filterFields(message.fields) : undefined;

  if (isInitialLoading) {
    return <Spinner />;
  }

  return (
    <InteractiveContext.Provider value={false}>
      <EvidenceCardFoundation
        title={
          <MessageSummary
            messageFields={filteredMessageFields}
            renderMessageRow={() => filteredMessageFields.message}
            hideOverflow
          />
        }
        onResetShowSimilar={onResetShowSimilar}
        actions={<MessagePermalinkButton messageIndex={message?.index} messageId={message?.id} />}
        onShowSimilar={onShowSimilar}
        hasSimilarFieldsFilter={!!similarFieldsFilter}
        evidence={evidence}
        isHighlighted={similarFieldsFilter?.targetEvidence.id === evidence.id}>
        {filteredMessageFields && (
          <LogCardFields
            fields={filteredMessageFields}
            similarFieldsFilter={similarFieldsFilter}
            evidenceId={evidence.id}
          />
        )}
      </EvidenceCardFoundation>
    </InteractiveContext.Provider>
  );
};

export default EvidenceLogCard;
